import React from 'react';
import {
    Box,
    Button,
    IconButton,
    InputAdornment,
    MenuItem,
    Paper,
    TableCell,
    TableRow,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Typography,
} from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMagnifyingGlass, faXmark } from '@fortawesome/free-solid-svg-icons';

// Estilo "píldora" compartido por el buscador, los selectores y las fechas
const estiloPildora = {
    '& .MuiOutlinedInput-root': {
        borderRadius: 5,
        backgroundColor: '#fff',
    },
};

/**
 * Campo de búsqueda con lupa, botón para borrar y Esc para limpiar.
 * Se usa dentro de BarraFiltros y también suelto (por ejemplo, en cada panel de Tags).
 */
export function CampoBusqueda({ valor, onChange, placeholder = 'Buscar', sx }) {
    return (
        <TextField
            size="small"
            value={valor}
            onChange={(e) => onChange(e.target.value)}
            onKeyDown={(e) => {
                if (e.key === 'Escape') onChange('');
            }}
            placeholder={placeholder}
            sx={{ ...estiloPildora, ...sx }}
            slotProps={{
                htmlInput: { 'aria-label': placeholder },
                input: {
                    startAdornment: (
                        <InputAdornment position="start">
                            <FontAwesomeIcon icon={faMagnifyingGlass} color="#6b7280" />
                        </InputAdornment>
                    ),
                    endAdornment:
                        valor !== '' ? (
                            <InputAdornment position="end">
                                <IconButton
                                    size="small"
                                    edge="end"
                                    aria-label="Borrar búsqueda"
                                    onClick={() => onChange('')}
                                >
                                    <FontAwesomeIcon icon={faXmark} />
                                </IconButton>
                            </InputAdornment>
                        ) : null,
                },
            }}
        />
    );
}

/**
 * Barra de filtros reutilizable.
 *
 * Props:
 *  - busqueda / onBusqueda: texto del buscador y su setter (opcionales: sin ellos no se muestra buscador).
 *  - placeholder: texto de ayuda; conviene decir en qué campos se busca.
 *  - filtros: lista opcional de filtros extra. Cada uno:
 *      { id, tipo: 'select' | 'chips' | 'fecha', etiqueta, valor, onChange,
 *        opciones: [{ valor, etiqueta }], textoTodos }
 *    El valor "sin filtro" es siempre '' (cadena vacía).
 *  - total / mostrados: cantidad total de registros y cuántos se ven ahora.
 *  - contador: texto propio para el contador (reemplaza al automático).
 *  - activoExtra: true si hay filtros activos fuera de esta barra (para mostrar "Limpiar filtros").
 *  - onLimpiar: función que deja búsqueda y filtros como al inicio.
 *  - sx: estilos extra para el contenedor (opcional).
 */
function BarraFiltros({
    busqueda,
    onBusqueda,
    placeholder = 'Buscar',
    filtros = [],
    total,
    mostrados,
    contador,
    activoExtra = false,
    onLimpiar,
    sx,
}) {
    const texto = busqueda ?? '';
    const hayAlgoActivo =
        Boolean(activoExtra) || texto.trim() !== '' || filtros.some((f) => f.valor !== '');

    const palabra = total === 1 ? 'registro' : 'registros';
    let textoContador = contador;
    if (textoContador === undefined && total !== undefined) {
        textoContador =
            hayAlgoActivo && mostrados !== total
                ? `${mostrados} de ${total} ${palabra}`
                : `${total} ${palabra}`;
    }

    return (
        <Paper
            square
            elevation={0}
            sx={{
                backgroundColor: '#f6f8fb',
                borderBottom: '1px solid #e3e8ef',
                px: 2,
                py: 1.5,
                ...sx,
            }}
        >
            <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1.5 }}>
                {onBusqueda && (
                    <CampoBusqueda
                        valor={texto}
                        onChange={onBusqueda}
                        placeholder={placeholder}
                        sx={{ flex: '1 1 280px', maxWidth: 400 }}
                    />
                )}

                {filtros.map((f) => {
                    if (f.tipo === 'chips') {
                        return (
                            <Box key={f.id} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Typography variant="body2" color="text.secondary">
                                    {f.etiqueta}
                                </Typography>
                                <ToggleButtonGroup
                                    exclusive
                                    size="small"
                                    value={f.valor}
                                    onChange={(e, nuevo) => {
                                        if (nuevo !== null) f.onChange(nuevo);
                                    }}
                                    aria-label={f.etiqueta}
                                    sx={{
                                        backgroundColor: '#fff',
                                        '& .MuiToggleButton-root': {
                                            textTransform: 'none',
                                            px: 1.5,
                                            py: 0.4,
                                        },
                                    }}
                                >
                                    {f.opciones.map((o) => (
                                        <ToggleButton key={o.valor} value={o.valor}>
                                            {o.etiqueta}
                                        </ToggleButton>
                                    ))}
                                </ToggleButtonGroup>
                            </Box>
                        );
                    }

                    if (f.tipo === 'fecha') {
                        return (
                            <TextField
                                key={f.id}
                                type="date"
                                size="small"
                                label={f.etiqueta}
                                value={f.valor}
                                onChange={(e) => f.onChange(e.target.value)}
                                slotProps={{ inputLabel: { shrink: true } }}
                                sx={{ ...estiloPildora, width: 170 }}
                            />
                        );
                    }

                    return (
                        <TextField
                            key={f.id}
                            select
                            size="small"
                            label={f.etiqueta}
                            value={f.valor}
                            onChange={(e) => f.onChange(e.target.value)}
                            sx={{ ...estiloPildora, minWidth: 190 }}
                        >
                            <MenuItem value="">{f.textoTodos || 'Todos'}</MenuItem>
                            {f.opciones.map((o) => (
                                <MenuItem key={o.valor} value={o.valor}>
                                    {o.etiqueta}
                                </MenuItem>
                            ))}
                        </TextField>
                    );
                })}

                <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 1 }}>
                    {textoContador !== undefined && (
                        <Typography variant="body2" color="text.secondary" role="status" aria-live="polite">
                            {textoContador}
                        </Typography>
                    )}
                    {hayAlgoActivo && (
                        <Button size="small" onClick={onLimpiar} sx={{ textTransform: 'none' }}>
                            Limpiar filtros
                        </Button>
                    )}
                </Box>
            </Box>
        </Paper>
    );
}

/**
 * Mensaje para cuando la lista queda vacía.
 *  - Con colSpan: devuelve una fila de tabla (usar dentro de <TableBody>).
 *  - Sin colSpan: devuelve un bloque suelto (para listas de tarjetas).
 *  - total === 0 significa que aún no hay datos; si no, es que los filtros no coinciden.
 */
export function SinResultados({ colSpan, total, onLimpiar, entidad = 'registros' }) {
    const sinDatos = total === 0;

    const contenido = (
        <Box sx={{ py: 4, textAlign: 'center', width: '100%' }}>
            <Typography variant="subtitle1">
                {sinDatos ? `Todavía no hay ${entidad}` : 'No encontramos resultados'}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                {sinDatos
                    ? 'Cuando agregues uno, aparecerá aquí.'
                    : 'Prueba con otra búsqueda o quita los filtros.'}
            </Typography>
            {!sinDatos && onLimpiar && (
                <Button size="small" onClick={onLimpiar} sx={{ mt: 1, textTransform: 'none' }}>
                    Limpiar filtros
                </Button>
            )}
        </Box>
    );

    if (colSpan) {
        return (
            <TableRow>
                <TableCell colSpan={colSpan}>{contenido}</TableCell>
            </TableRow>
        );
    }
    return contenido;
}

export default BarraFiltros;
